import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { StartPage, SearchComponent, FilterBar, Button } from "../../../components";
import { useAuth } from "../../../context/user.context";
import { icons } from "../../../icons";
import { timeConverterMMDDYYHHMM } from "../../../utils/dates";
import { useNavigate } from "react-router-dom";
import { useUi } from "../../../context/ui.context";
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import { clientService } from "../../../api";
import useUpdateEffect from "../../../hooks/useUpdateEffect";
import { usePageTitle } from "../../../context/navigationContext";
const CLIENT_FILTER_KEYS = [
  'status',
  'country',
  'date_created',
  'configuration',
  'tax_exempt',
  'groups',
  'services__openstack_project__instance__region',
  'services__openstack_project__instance__flavor',
  'services__openstack_project__instance__host_name',
  'has_billing_agreement',
  'has_services',
  'has_instances',
  'has_reseller_services',
  'assigned_to_reseller',
  'services__product__product_type',
  'services__product',
];
const DEFAULT_ORDERING = '-date_created';

const Clients:FC = () => {
  const {user, updateUser} = useAuth()
  const [rows, setRows] = useState<any[]>([])
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize, setPageSize] = useQueryParam('page_size', NumberParam);
  const [searchString, setSearchString] = useQueryParam('search', StringParam);
  const [filtering, setFiltering] = useQueryParam('filtering', StringParam);
  const {viewAlert} = useUi()
  const [paginationData, setPaginationData] = useState<any>()
  const [t] = useTranslation()
  usePageTitle(t("PAGE_TITLE_CLIENTS"));
  const [filterOptions, setFilterOptions] = useState<any>()
  const normalizedFilterOptions = useMemo(() => {
    const base: Record<string, any> = {};
    if (filterOptions) {
      CLIENT_FILTER_KEYS.forEach((key) => {
        if (Array.isArray(filterOptions?.[key]) && filterOptions?.[key]?.length) {
          base[key] = filterOptions[key];
        }
      });
    }
    const booleanOptions = [
      { display: t("YES"), value: 'true' },
      { display: t("NO"), value: 'false' },
    ];
    [
      'tax_exempt',
      'has_billing_agreement',
      'has_services',
      'has_instances',
      'has_reseller_services',
      'assigned_to_reseller',
    ].forEach((key) => {
      if (!base[key]) {
        base[key] = booleanOptions;
      }
    });
    // Add searchable filters with empty initial options
    // These will be populated by FilterBar's remote search
    if (!base.configuration) {
      base.configuration = [];
    }
    if (!base.groups) {
      base.groups = [];
    }
    return base;
  }, [filterOptions, t]);
  const handleResetFilters = () => {
    setSearchString(undefined);
    setFiltering(undefined);
    setOrdering(DEFAULT_ORDERING);
    setPage(1);
  }

  const updateList = async () => {
    try{
      const res = await clientService.getClientList({
        page:page||1,
        ordering: ordering || DEFAULT_ORDERING,
        pageSize:pageSize||10,
        search:searchString||'',
        filtering: filtering || '',
      })
      let data = res.data.objects.map((client:any) => {
        return {
        ...client,
        id:client.id,
        // long_name:client.long_name,
        navigate:`${client.id}`,
        // roles:client.roles.length > 0 ? client.roles.map((role:any) => role.display_name) : '',
        date_created:timeConverterMMDDYYHHMM(client.date_created),
      }})
      setPaginationData(res.data)
      setFilterOptions(res.data?.filter_options);
      setRows(data)
      setPaginationData(res.data);
      setPageLoading(false)

    } catch(e:any){
      if(e?.response?.data?.detail){
        viewAlert({
          severity:'error',
          message:e?.response?.data?.detail
        })
      }
      setPageLoading(true)

    }
    
  }
  const navigate = useNavigate()
  const canDissociate = (id:any) => {
    
  }

  useEffect(() => {
    updateList()
  },[page, ordering, pageSize, searchString, filtering])
  useUpdateEffect(() => {
    setPage(1)
  }, [pageSize, filtering])
  useEffect(() => {
    if (!ordering) {
      setOrdering(DEFAULT_ORDERING);
    }
  }, [ordering, setOrdering]);
  const items = [
    {
      title:t("EDIT"),
      id:'2',
      icon:icons.Edit,
      onClick: (item:any) => navigate(`${item.id}/edit`),
      divider:false
    },
    {
      title:t("DISSOCIATE"),
      id:'4',
      onClick:(item:any) => canDissociate(item.id),
      icon:icons.MinusDefault,
      divider:false
    },
  ]

  const columns = [
    {
      title:t("ID"),
      key:"id",
      sorted:true,
    },
    {
      title:t("NAME"),
      key:'long_name',
      sorted:true,
    },
    {
      title:t("YOUR_ROLES"),
      key:"roles",
      withBackground:true,
    },
    {
      title:t("CREATED_AT"),
      key:"date_created",
      sorted:true,
    },
    {
      title:"",
      key:'props'
    }
  ]
  return(
    <>
      <StartPage
        title={`${t("CLIENTS")}`}
        noResourceText={`${t("NO_CLIENTS")}`}
        createResourceButton={`${t('CREATE_CLIENTS')}`}
        createResourceLink="/clients-users/clients/create"
        haveResource={rows.length > 0}
        pageLoading={pageLoading}
        withoutCreateResourceButton
        rightCustomCreateButton={
          <div style={{display:'flex', flexDirection:'column', gap:'10px', width:'100%'}}>
            <div style={{display:'flex', alignItems:'center', gap:'12px', width:'100%'}}>
              <Button
                title={t('CREATE_CLIENTS')}
                onClick={() => navigate('/clients-users/clients/create')}
                variant='primary'
                size='custom'
                customStyles={{height:'40px', whiteSpace:'nowrap'}}
              />
              <div style={{flex:1, minWidth:'200px'}}>
                <SearchComponent />
              </div>
              <div style={{display:'flex', alignItems:'center', gap:'8px', marginLeft:'auto'}}>
                <Button
                  title={t("CLEAR_FILTERS")}
                  onClick={handleResetFilters}
                  variant='stroke'
                  size='display'
                  disabled={
                    (!filtering || filtering === '') &&
                    !searchString &&
                    ordering === DEFAULT_ORDERING
                  }
                />
              </div>
            </div>
            {normalizedFilterOptions ? (
              <FilterBar filterOptions={normalizedFilterOptions} />
            ) : null}
          </div>
        }
        tableData={{
          columns:columns,
          data:rows,
          hideCheckbox:true,
          props:items,
          paginationData:paginationData,
          updateData:updateList
        }}
      />
    </>
  )
}

export default Clients
